import { RESULTS_DATA } from '@/features/landing-page/helpers/landing-results-data'
import { PUBLIC_LANDING_ROUTES } from '@/setup/routing/routing'

/**
 * View-model for the lp-v3 case-study cards. Brand, logo, the headline stat and the case-study link
 * are REUSED from the canonical `RESULTS_DATA` (single source of truth, shared with the old section).
 * Two things are lp-v3-specific and live here: which of a brand's stats is the "hero" (the designs
 * pick the strongest one, not always the first) and a short teaser derived from the brand's real
 * numbers. Edit the teasers freely — they are the only new copy.
 */
export type CaseStudyCard = {
    id: string
    company: string
    logoUrl: string
    statValue: string
    statLabel: string
    summary: string
    /** `/case-study/<id>` — omitted for brands without a published case study page. */
    href?: string
}

/** Card order + hero-stat pick + teaser. `heroStatIndex` selects which entry of `stats` to feature. */
const CARD_CONFIG: { id: string; heroStatIndex: number; summary: string }[] = [
    { id: 'taxfix', heroStatIndex: 1, summary: 'How Taxfix lifted click-through rate up to 45% with high-velocity creative production.' },
    { id: 'sumup', heroStatIndex: 0, summary: 'How SumUp scaled 120+ Meta ads across products in 8+ languages.' },
    { id: 'marketbirds', heroStatIndex: 0, summary: 'How agency marketbirds grew creative output 540% and lifted click-through rate 26%.' },
    { id: 'lila', heroStatIndex: 0, summary: 'How Lila cut cost-per-trial 6× and halved CPI with AI UGC creative.' },
    { id: 'advercy', heroStatIndex: 0, summary: 'How agency Advercy cut cost-per-lead 50% and slashed UGC production cost 95%.' },
    { id: 'stromnow', heroStatIndex: 0, summary: 'How StromNow produced video 20× cheaper and doubled app downloads.' },
    { id: 'aligned', heroStatIndex: 0, summary: 'How Aligned lifted click-through rate 160% and hook rate 105% with faster creative.' },
    { id: 'ordio', heroStatIndex: 1, summary: 'How Ordio cut Meta CPL up to 70% by validating more creatives every week.' },
]

const RESULT_BY_ID = new Map(RESULTS_DATA.map((result) => [result.id, result]))

export const CASE_STUDY_CARDS: CaseStudyCard[] = CARD_CONFIG.flatMap(({ id, heroStatIndex, summary }) => {
    const result = RESULT_BY_ID.get(id)
    if (!result) return []

    const stat = result.stats[heroStatIndex] ?? result.stats[0]
    // `hasCaseStudyLink` is opt-out: undefined means the page exists; only `false` hides the link.
    const href = result.hasCaseStudyLink === false ? undefined : `${PUBLIC_LANDING_ROUTES.CASE_STUDY}/${result.id}`

    return [
        {
            id: result.id,
            company: result.company,
            logoUrl: result.logoUrl,
            statValue: stat.value,
            statLabel: stat.label,
            summary,
            href,
        },
    ]
})
